import { useCallback, useEffect, useRef, useState } from "react";
import {
  enablePushNotificationsFromUserGesture,
  fetchPushServerEnabled,
  forceResubscribePush,
  pushNotificationsSupported,
} from "../lib/push";
import type { AvatarUrls } from "../lib/avatarDisplay";
import { avatarTerminalTintStyle } from "../lib/avatarDisplay";
import { apiDeleteAvatar, apiUploadAvatar } from "../lib/authApi";
import { USERNAME_MAX_LEN } from "../app/state/appUtils";
import { useTheme } from "../app/context/ThemeContext";
import {
  DEFAULT_MAX_ENTRY_MB,
  DEFAULT_MAX_TOTAL_MB,
  loadAttachmentCacheSettings,
  maxEntryMbCap,
  MAX_TOTAL_MB,
  MIN_ENTRY_MB,
  MIN_TOTAL_MB,
  saveAttachmentCacheSettings,
} from "../lib/attachmentCacheSettings";
import { enforceAttachmentCacheBudget } from "../lib/chatImageDiskCache";
import { AppIcon } from "./AppIcon";
import { STYX_THEME } from "../styles/theme";
import { useVault } from "../app/context/VaultContext";

type SettingsPanelProps = {
  onClose: () => void;
  onLogout: () => void;
  username: string;
  currentAvatarUrl: string | null;
  onAvatarChanged: (avatars: AvatarUrls | null) => void;
  onRenameUsername?: (next: string) => Promise<void>;
  renameError?: string | null;
  onRenameBegin?: () => void;
};

export function SettingsPanel({
  onClose,
  onLogout,
  username,
  currentAvatarUrl,
  onAvatarChanged,
  onRenameUsername,
  renameError,
  onRenameBegin,
}: SettingsPanelProps) {
  const { preference, presets, currentPreset, visualMode, setPreset, setVisualMode, updatePalette, updateTactility, resetTheme } = useTheme();
  const { hasPin, autoLockMinutes, setAutoLockMinutes, changePin } = useVault();
  const [vaultPinOld, setVaultPinOld] = useState("");
  const [vaultPinNew, setVaultPinNew] = useState("");
  const [vaultPinNew2, setVaultPinNew2] = useState("");
  const [vaultPinBusy, setVaultPinBusy] = useState(false);
  const [vaultPinMsg, setVaultPinMsg] = useState<string | null>(null);
  const [pushServerOk, setPushServerOk] = useState<boolean | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [endpointPreview, setEndpointPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [renameDraft, setRenameDraft] = useState(username);
  const [renameBusy, setRenameBusy] = useState(false);

  const entryCapMb = maxEntryMbCap();
  const [cacheEntryStr, setCacheEntryStr] = useState(String(DEFAULT_MAX_ENTRY_MB));
  const [cacheTotalStr, setCacheTotalStr] = useState(String(DEFAULT_MAX_TOTAL_MB));

  const refreshCacheInputs = useCallback(() => {
    const s = loadAttachmentCacheSettings();
    setCacheEntryStr(String(s.maxEntryMb));
    setCacheTotalStr(String(s.maxTotalMb));
  }, []);

  useEffect(() => {
    refreshCacheInputs();
  }, [refreshCacheInputs]);

  useEffect(() => {
    window.addEventListener("styx-attachment-cache-settings", refreshCacheInputs);
    return () => window.removeEventListener("styx-attachment-cache-settings", refreshCacheInputs);
  }, [refreshCacheInputs]);

  const commitCacheEntryMb = useCallback(() => {
    const v = parseInt(cacheEntryStr, 10);
    if (!Number.isFinite(v)) {
      refreshCacheInputs();
      return;
    }
    const saved = saveAttachmentCacheSettings({ maxEntryMb: v });
    setCacheEntryStr(String(saved.maxEntryMb));
    setCacheTotalStr(String(saved.maxTotalMb));
    void enforceAttachmentCacheBudget();
  }, [cacheEntryStr, refreshCacheInputs]);

  const commitCacheTotalMb = useCallback(() => {
    const v = parseInt(cacheTotalStr, 10);
    if (!Number.isFinite(v)) {
      refreshCacheInputs();
      return;
    }
    const saved = saveAttachmentCacheSettings({ maxTotalMb: v });
    setCacheEntryStr(String(saved.maxEntryMb));
    setCacheTotalStr(String(saved.maxTotalMb));
    void enforceAttachmentCacheBudget();
  }, [cacheTotalStr, refreshCacheInputs]);

  useEffect(() => {
    setRenameDraft(username);
  }, [username]);

  useEffect(() => {
    onRenameBegin?.();
  }, []);

  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  const refreshPushState = useCallback(async () => {
    void fetchPushServerEnabled().then(setPushServerOk);
    if (typeof Notification !== "undefined") {
      setPermission(Notification.permission);
    }
    if (!pushNotificationsSupported() || !("serviceWorker" in navigator)) {
      setEndpointPreview(null);
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setEndpointPreview(sub ? `${sub.endpoint.slice(0, 72)}…` : null);
    } catch {
      setEndpointPreview(null);
    }
  }, []);

  useEffect(() => {
    void refreshPushState();
  }, [refreshPushState]);

  const onEnablePush = useCallback(() => {
    setMessage(null);
    setBusy(true);
    void (async () => {
      const r = await enablePushNotificationsFromUserGesture();
      setBusy(false);
      if (!r.ok && r.message) setMessage(r.message);
      await refreshPushState();
    })();
  }, [refreshPushState]);

  const onForceResubscribe = useCallback(() => {
    setMessage(null);
    setBusy(true);
    void (async () => {
      const r = await forceResubscribePush();
      setBusy(false);
      if (!r.ok && r.message) setMessage(r.message);
      else if (r.ok) setMessage("Подписка обновлена.");
      await refreshPushState();
    })();
  }, [refreshPushState]);

  const onFilePick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarMsg(null);
    setAvatarBusy(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return previewUrl;
      });
      setAvatarBlob(file);
    } catch {
      setAvatarMsg("Не удалось обработать изображение");
    }
    setAvatarBusy(false);
    e.target.value = "";
  }, []);

  const onSaveAvatar = useCallback(async () => {
    if (!avatarBlob) return;
    setAvatarBusy(true);
    setAvatarMsg(null);
    try {
      const { avatar } = await apiUploadAvatar(avatarBlob);
      onAvatarChanged(avatar);
      setAvatarBlob(null);
      setAvatarPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setAvatarMsg("Аватар сохранён.");
    } catch (err) {
      setAvatarMsg((err as Error).message);
    }
    setAvatarBusy(false);
  }, [avatarBlob, onAvatarChanged]);

  const onRemoveAvatar = useCallback(async () => {
    setAvatarBusy(true);
    setAvatarMsg(null);
    try {
      await apiDeleteAvatar();
      onAvatarChanged(null);
      setAvatarMsg("Аватар удалён.");
    } catch (err) {
      setAvatarMsg((err as Error).message);
    }
    setAvatarBusy(false);
  }, [onAvatarChanged]);

  const submitRename = useCallback(async () => {
    if (!onRenameUsername) return;
    setRenameBusy(true);
    try {
      await onRenameUsername(renameDraft);
    } catch {
      /* parent sets renameError */
    } finally {
      setRenameBusy(false);
    }
  }, [onRenameUsername, renameDraft]);

  const permLabel: Record<NotificationPermission, string> = {
    granted: "разрешены",
    denied: "заблокированы",
    default: "не запрошены",
  };

  const initial = username.charAt(0).toUpperCase();
  const displayUrl = avatarPreview ?? currentAvatarUrl;
  const themeLinks = [
    { href: "/theme-ui-kit", label: "UI kit" },
    { href: "/theme-preview", label: "2 themes" },
    { href: "/theme-lab", label: "Theme lab" },
  ];
  const tactilityPercent = Math.round(preference.tactility * 100);

  return (
    <div className="settings-panel">
      <div className="settings-panel__head">
        <h2 className="settings-panel__title">Настройки</h2>
        <button type="button" className="settings-panel__close" onClick={onClose} aria-label="Закрыть">
          <AppIcon name="x" size={18} />
        </button>
      </div>

      <section className="settings-panel__section" style={{ marginBottom: 24 }}>
        <h3 className="settings-panel__h3">Оформление</h3>

        <div className="settings-panel__mode-toggle" style={{ marginBottom: 14 }}>
          <button
            type="button"
            className={`settings-panel__mode-btn ${visualMode === "terminal" ? "is-active" : ""}`}
            onClick={() => setVisualMode("terminal")}
          >
            Terminal
          </button>
          <button
            type="button"
            className={`settings-panel__mode-btn ${visualMode === "modern" ? "is-active" : ""}`}
            onClick={() => setVisualMode("modern")}
          >
            Modern
          </button>
        </div>

        <p className="settings-panel__muted" style={{ marginBottom: 12 }}>
          {visualMode === "terminal"
            ? "Неоновое свечение, моноширинный шрифт, прямые углы — классический терминальный стиль."
            : "Чистый шрифт, скруглённые углы, без свечения — спокойный современный стиль."}
        </p>

        <div className="settings-panel__theme-presets">
          {presets.filter((p) => p.visualMode === visualMode).map((preset) => {
            const active = currentPreset?.id === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                className={`settings-panel__preset ${active ? "is-active" : ""}`}
                onClick={() => setPreset(preset.id)}
              >
                <span className="settings-panel__preset-head">
                  <span className="settings-panel__preset-title">{preset.label}</span>
                  {active ? <span className="settings-panel__preset-badge">active</span> : null}
                </span>
                <span className="settings-panel__preset-desc">{preset.description}</span>
                <span className="settings-panel__preset-swatches" aria-hidden="true">
                  {Object.values(preset.palette).map((color) => (
                    <span key={color} className="settings-panel__preset-swatch" style={{ background: color }} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>

        <div className="settings-panel__theme-grid">
          <label className="settings-panel__theme-field">
            <span className="settings-panel__label">Фон</span>
            <div className="settings-panel__color-row">
              <input
                type="color"
                className="settings-panel__color"
                value={preference.palette.background}
                onChange={(e) => updatePalette({ background: e.target.value })}
                aria-label="Цвет фона"
              />
              <span className="settings-panel__color-value">{preference.palette.background}</span>
            </div>
          </label>
          <label className="settings-panel__theme-field">
            <span className="settings-panel__label">Панель</span>
            <div className="settings-panel__color-row">
              <input
                type="color"
                className="settings-panel__color"
                value={preference.palette.panel}
                onChange={(e) => updatePalette({ panel: e.target.value })}
                aria-label="Цвет панелей"
              />
              <span className="settings-panel__color-value">{preference.palette.panel}</span>
            </div>
          </label>
          <label className="settings-panel__theme-field">
            <span className="settings-panel__label">Акцент</span>
            <div className="settings-panel__color-row">
              <input
                type="color"
                className="settings-panel__color"
                value={preference.palette.accent}
                onChange={(e) => updatePalette({ accent: e.target.value })}
                aria-label="Цвет акцента"
              />
              <span className="settings-panel__color-value">{preference.palette.accent}</span>
            </div>
          </label>
          <label className="settings-panel__theme-field">
            <span className="settings-panel__label">Текст</span>
            <div className="settings-panel__color-row">
              <input
                type="color"
                className="settings-panel__color"
                value={preference.palette.text}
                onChange={(e) => updatePalette({ text: e.target.value })}
                aria-label="Цвет текста"
              />
              <span className="settings-panel__color-value">{preference.palette.text}</span>
            </div>
          </label>
          <label className="settings-panel__theme-field">
            <span className="settings-panel__label">Тактильность</span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={tactilityPercent}
              onChange={(e) => updateTactility(Number(e.target.value) / 100)}
              aria-label="Тактильность поверхности"
            />
            <span className="settings-panel__color-value">{tactilityPercent}%</span>
            <span className="settings-panel__muted" style={{ marginBottom: 0 }}>
              Управляет зерном и ощущением фактуры на панелях.
            </span>
          </label>
        </div>

        <div className="settings-panel__actions">
          <button type="button" className="settings-panel__btn" onClick={resetTheme}>
            Сбросить тему
          </button>
          {themeLinks.map((link) => (
            <a key={link.href} className="settings-panel__btn settings-panel__btn--link" href={link.href}>
              {link.label}
            </a>
          ))}
        </div>

        <div className="settings-panel__theme-preview" aria-hidden="true">
          <div className="settings-panel__preview-bar">
            <span className="settings-panel__preview-brand">{STYX_THEME.appName}</span>
            <span className="settings-panel__preview-meta">theme preview</span>
          </div>
          <div className="settings-panel__preview-body">
            <div className="settings-panel__preview-bubble settings-panel__preview-bubble--them">
              <span className="settings-panel__preview-author">@relay</span>
              <span>Новая палитра автоматически разложилась на панели, текст и подсветку.</span>
            </div>
            <div className="settings-panel__preview-bubble settings-panel__preview-bubble--me">
              <span>Выглядит как отдельная тема, а не просто другой accent.</span>
            </div>
          </div>
        </div>
      </section>

      <section className="settings-panel__section" style={{ marginBottom: 24 }}>
        <h3 className="settings-panel__h3">Аватар</h3>
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12 }}>
          <div className="contact-avatar" style={{ width: 64, height: 64 }}>
            {displayUrl ? (
              <img
                src={displayUrl}
                className={`contact-avatar__img${visualMode === "terminal" && !avatarPreview ? " contact-avatar__img--terminal" : ""}`}
                style={
                  visualMode === "terminal" && !avatarPreview ? avatarTerminalTintStyle(preference.palette.accent) : undefined
                }
                alt=""
              />
            ) : (
              <span className="contact-avatar__letter" style={{ fontSize: 24 }}>{initial}</span>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              type="button"
              className="settings-panel__btn settings-panel__btn--accent"
              disabled={avatarBusy}
              onClick={() => fileRef.current?.click()}
            >
              Загрузить фото
            </button>
            {currentAvatarUrl && !avatarPreview ? (
              <button
                type="button"
                className="settings-panel__btn"
                disabled={avatarBusy}
                onClick={onRemoveAvatar}
              >
                Удалить
              </button>
            ) : null}
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={onFilePick} style={{ display: "none" }} />
        {avatarPreview && avatarBlob ? (
          <div className="settings-panel__actions">
            <button
              type="button"
              className="settings-panel__btn settings-panel__btn--accent"
              disabled={avatarBusy}
              onClick={onSaveAvatar}
            >
              Сохранить
            </button>
            <button
              type="button"
              className="settings-panel__btn"
              disabled={avatarBusy}
              onClick={() => {
                setAvatarPreview((prev) => {
                  if (prev) URL.revokeObjectURL(prev);
                  return null;
                });
                setAvatarBlob(null);
              }}
            >
              Отменить
            </button>
          </div>
        ) : null}
        {avatarBusy ? <p className="settings-panel__muted">Обработка…</p> : null}
        {avatarMsg ? <p className="settings-panel__msg">{avatarMsg}</p> : null}

        {onRenameUsername ? (
          <>
            <h3 className="settings-panel__h3" style={{ marginTop: 24 }}>
              Имя пользователя
            </h3>
            <p className="settings-panel__muted" style={{ marginBottom: 10 }}>
              Логин без @; латиница, цифры и подчёркивание, до {USERNAME_MAX_LEN} символов.
            </p>
            <input
              className="settings-panel__input"
              value={renameDraft}
              maxLength={USERNAME_MAX_LEN}
              onChange={(e) => {
                onRenameBegin?.();
                setRenameDraft(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void submitRename();
                }
              }}
              disabled={renameBusy}
              autoComplete="username"
              spellCheck={false}
              aria-label="Имя пользователя"
            />
            <div className="settings-panel__actions">
              <button
                type="button"
                className="settings-panel__btn settings-panel__btn--accent"
                disabled={renameBusy}
                onClick={() => void submitRename()}
              >
                Сохранить имя
              </button>
            </div>
            {renameError ? <p className="settings-panel__err">{renameError}</p> : null}
          </>
        ) : null}
      </section>

      {hasPin ? (
        <section className="settings-panel__section" style={{ marginBottom: 24 }}>
          <h3 className="settings-panel__h3">Хранилище</h3>
          <p className="settings-panel__muted" style={{ marginBottom: 10 }}>
            Автоблокировка списка Vault на этом устройстве после простоя.
          </p>
          <label className="settings-panel__theme-field">
            <span className="settings-panel__label">Автоблокировка</span>
            <select
              className="settings-panel__input"
              value={autoLockMinutes === null ? "never" : String(autoLockMinutes)}
              onChange={(e) => {
                const v = e.target.value;
                void setAutoLockMinutes(v === "never" ? null : Number(v));
              }}
            >
              <option value="5">5 минут</option>
              <option value="10">10 минут</option>
              <option value="15">15 минут</option>
              <option value="30">30 минут</option>
              <option value="60">60 минут</option>
              <option value="never">Никогда не блокировать</option>
            </select>
          </label>

          <h4 className="settings-panel__h3" style={{ marginTop: 20, fontSize: "1rem" }}>
            Сменить пин-код
          </h4>
          <p className="settings-panel__muted" style={{ marginBottom: 8 }}>
            5 цифр. Сначала текущий пин, затем новый дважды.
          </p>
          <input
            className="settings-panel__input"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={5}
            placeholder="Текущий пин"
            value={vaultPinOld}
            onChange={(e) => setVaultPinOld(e.target.value.replace(/\D/g, "").slice(0, 5))}
            style={{ marginBottom: 8 }}
          />
          <input
            className="settings-panel__input"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={5}
            placeholder="Новый пин"
            value={vaultPinNew}
            onChange={(e) => setVaultPinNew(e.target.value.replace(/\D/g, "").slice(0, 5))}
            style={{ marginBottom: 8 }}
          />
          <input
            className="settings-panel__input"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={5}
            placeholder="Повтор нового пина"
            value={vaultPinNew2}
            onChange={(e) => setVaultPinNew2(e.target.value.replace(/\D/g, "").slice(0, 5))}
            style={{ marginBottom: 8 }}
          />
          <div className="settings-panel__actions">
            <button
              type="button"
              className="settings-panel__btn settings-panel__btn--accent"
              disabled={
                vaultPinBusy ||
                vaultPinOld.length !== 5 ||
                vaultPinNew.length !== 5 ||
                vaultPinNew2.length !== 5
              }
              onClick={() => {
                setVaultPinMsg(null);
                if (vaultPinNew !== vaultPinNew2) {
                  setVaultPinMsg("Новый пин и повтор не совпадают");
                  return;
                }
                setVaultPinBusy(true);
                void changePin(vaultPinOld, vaultPinNew)
                  .then(() => {
                    setVaultPinOld("");
                    setVaultPinNew("");
                    setVaultPinNew2("");
                    setVaultPinMsg("Пин обновлён");
                  })
                  .catch((e: unknown) => {
                    const msg = e instanceof Error ? e.message : String(e);
                    setVaultPinMsg(msg);
                  })
                  .finally(() => setVaultPinBusy(false));
              }}
            >
              Сохранить новый пин
            </button>
          </div>
          {vaultPinBusy ? <p className="settings-panel__muted">Сохранение…</p> : null}
          {vaultPinMsg ? <p className="settings-panel__msg">{vaultPinMsg}</p> : null}
        </section>
      ) : null}

      <section className="settings-panel__section" style={{ marginBottom: 24 }}>
        <h3 className="settings-panel__h3">Кэш вложений</h3>
        <p className="settings-panel__muted" style={{ marginBottom: 12 }}>
          Локально в браузере (зашифровано на устройстве): фото в ленте подгружаются для превью; файлы попадают в кэш
          только после «Скачать» или «Открыть». По умолчанию не кэшируются вложения больше {DEFAULT_MAX_ENTRY_MB} МБ;
          общий потолок кэша по умолчанию {DEFAULT_MAX_TOTAL_MB} МБ, максимум {MAX_TOTAL_MB} МБ.
        </p>
        <div className="settings-panel__theme-grid">
          <label className="settings-panel__theme-field">
            <span className="settings-panel__label">Макс. размер одного вложения (МБ)</span>
            <input
              type="number"
              className="settings-panel__input"
              min={MIN_ENTRY_MB}
              max={entryCapMb}
              step={1}
              value={cacheEntryStr}
              onChange={(e) => setCacheEntryStr(e.target.value)}
              onBlur={() => void commitCacheEntryMb()}
              aria-label="Максимальный размер одного вложения в кэше в мегабайтах"
            />
            <span className="settings-panel__muted" style={{ fontSize: 12 }}>
              от {MIN_ENTRY_MB} до {entryCapMb} (не больше лимита отправки)
            </span>
          </label>
          <label className="settings-panel__theme-field">
            <span className="settings-panel__label">Макс. размер всего кэша (МБ)</span>
            <input
              type="number"
              className="settings-panel__input"
              min={MIN_TOTAL_MB}
              max={MAX_TOTAL_MB}
              step={1}
              value={cacheTotalStr}
              onChange={(e) => setCacheTotalStr(e.target.value)}
              onBlur={() => void commitCacheTotalMb()}
              aria-label="Максимальный размер кэша вложений в мегабайтах"
            />
            <span className="settings-panel__muted" style={{ fontSize: 12 }}>
              от {MIN_TOTAL_MB} до {MAX_TOTAL_MB}
            </span>
          </label>
        </div>
      </section>

      <section className="settings-panel__section">
        <h3 className="settings-panel__h3">Push-уведомления</h3>
        {!pushNotificationsSupported() ? (
          <p className="settings-panel__muted">Браузер не поддерживает Web Push.</p>
        ) : (
          <>
            <p className="settings-panel__row">
              <span className="settings-panel__label">Сервер</span>
              <span>
                {pushServerOk === null ? "…" : pushServerOk ? "включён" : "не настроен"}
              </span>
            </p>
            <p className="settings-panel__row">
              <span className="settings-panel__label">Разрешение</span>
              <span>{permLabel[permission]}</span>
            </p>
            {endpointPreview ? (
              <p className="settings-panel__endpoint" title={endpointPreview}>
                {endpointPreview}
              </p>
            ) : (
              <p className="settings-panel__muted">Нет активной подписки push.</p>
            )}

            {permission === "denied" ? (
              <p className="settings-panel__hint">
                Разблокируйте уведомления для этого сайта в настройках браузера или ОС, затем обновите страницу.
              </p>
            ) : null}

            <div className="settings-panel__actions">
              {permission === "default" ? (
                <button type="button" className="settings-panel__btn settings-panel__btn--accent" disabled={busy} onClick={onEnablePush}>
                  Включить уведомления
                </button>
              ) : null}
              {permission === "granted" ? (
                <button type="button" className="settings-panel__btn" disabled={busy || pushServerOk !== true} onClick={onForceResubscribe}>
                  Переинициализировать уведомления
                </button>
              ) : null}
            </div>
            {message ? <p className="settings-panel__msg">{message}</p> : null}
          </>
        )}
      </section>

      <section className="settings-panel__section settings-panel__section--account">
        <button type="button" className="settings-panel__btn settings-panel__btn--danger" onClick={onLogout}>
          Выйти из аккаунта
        </button>
      </section>
    </div>
  );
}
